import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { 
  User, 
  Mail, 
  Phone, 
  Calendar, 
  BookOpen, 
  Heart, 
  Users, 
  AlertCircle,
  CheckCircle,
  ArrowLeft
} from 'lucide-react';
import { db } from '../../database/supabase';
import { useAuth } from '../../contexts/AuthContext';

const StudentNeedsAssessmentForm = () => {
  const { register, handleSubmit, formState: { errors }, watch } = useForm();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [currentSection, setCurrentSection] = useState(1);
  const navigate = useNavigate();
  const { user } = useAuth();

  const totalSections = 4;

  const onSubmit = async (data) => {
    setIsSubmitting(true);
    
    try {
      // Get the form template
      const { data: formTemplate, error: formError } = await db.getFormByType('student_needs_assessment');
      
      if (formError) {
        throw new Error('Could not load form template');
      }

      // Prepare submission data
      const submissionData = {
        form_id: formTemplate.id,
        user_id: user?.id || null,
        data: data,
        status: 'submitted',
        submitted_at: new Date().toISOString()
      };

      // Submit to database
      const { error: submitError } = await db.createSubmission(submissionData);
      
      if (submitError) {
        throw submitError;
      }

      toast.success('Form submitted successfully!');
      navigate('/forms', { 
        state: { 
          message: 'Your Student Needs Assessment has been submitted. A counselor will review it and contact you soon.' 
        }
      });
      
    } catch (error) {
      console.error('Submission error:', error);
      toast.error('Failed to submit form. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const nextSection = () => {
    if (currentSection < totalSections) {
      setCurrentSection(currentSection + 1);
    }
  };

  const prevSection = () => {
    if (currentSection > 1) {
      setCurrentSection(currentSection - 1);
    }
  };

  const renderProgressBar = () => (
    <div className="mb-8">
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-medium text-gray-700">Progress</span>
        <span className="text-sm text-gray-500">{currentSection} of {totalSections}</span>
      </div>
      <div className="w-full bg-gray-200 rounded-full h-2">
        <div 
          className="bg-blue-800 h-2 rounded-full transition-all duration-300"
          style={{ width: `${(currentSection / totalSections) * 100}%` }}
        ></div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="container-custom">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/forms')}
            className="flex items-center text-blue-800 hover:text-red-900 mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Forms
          </button>
          
          <div className="text-center">
            <h1 className="text-3xl font-bold text-gray-800 mb-2">
              Student Needs Assessment Form
            </h1>
            <p className="text-gray-600 max-w-2xl mx-auto">
              This assessment helps us understand your academic, personal, and social needs 
              so we can provide you with the best possible support and guidance.
            </p>
          </div>
        </div>

        <div className="form-container">
          {renderProgressBar()}

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
            {/* Section 1: Personal Information */}
            {currentSection === 1 && (
              <div className="form-section fade-in">
                <div className="flex items-center mb-6">
                  <User className="w-6 h-6 text-blue-800 mr-3" />
                  <h2 className="form-section-title">Personal Information</h2>
                </div>

                <div className="form-grid">
                  <div>
                    <label className="form-label">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      className="form-input"
                      {...register('fullName', { required: 'Full name is required' })}
                      placeholder="Enter your full name"
                    />
                    {errors.fullName && (
                      <p className="form-error">{errors.fullName.message}</p>
                    )}
                  </div>

                  <div>
                    <label className="form-label">
                      Student ID *
                    </label>
                    <input
                      type="text"
                      className="form-input"
                      {...register('studentId', { required: 'Student ID is required' })}
                      placeholder="Enter your student ID"
                    />
                    {errors.studentId && (
                      <p className="form-error">{errors.studentId.message}</p>
                    )}
                  </div>

                  <div>
                    <label className="form-label">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      className="form-input"
                      {...register('email', { 
                        required: 'Email is required',
                        pattern: {
                          value: /^\S+@\S+$/i,
                          message: 'Invalid email address'
                        }
                      })}
                      placeholder="Enter your email"
                    />
                    {errors.email && (
                      <p className="form-error">{errors.email.message}</p>
                    )}
                  </div>

                  <div>
                    <label className="form-label">
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      className="form-input"
                      {...register('phone')}
                      placeholder="Enter your phone number"
                    />
                  </div>

                  <div>
                    <label className="form-label">
                      Grade Level *
                    </label>
                    <select
                      className="form-input"
                      {...register('gradeLevel', { required: 'Grade level is required' })}
                    >
                      <option value="">Select grade level</option>
                      <option value="9">Grade 9</option>
                      <option value="10">Grade 10</option>
                      <option value="11">Grade 11</option>
                      <option value="12">Grade 12</option>
                    </select>
                    {errors.gradeLevel && (
                      <p className="form-error">{errors.gradeLevel.message}</p>
                    )}
                  </div>

                  <div>
                    <label className="form-label">
                      Program/Track
                    </label>
                    <select className="form-input" {...register('program')}>
                      <option value="">Select program</option>
                      <option value="STEM">STEM</option>
                      <option value="ABM">ABM</option>
                      <option value="HUMSS">HUMSS</option>
                      <option value="GAS">GAS</option>
                      <option value="TVL">TVL</option>
                    </select>
                  </div>
                </div>
              </div>
            )}

            {/* Section 2: Academic Needs */}
            {currentSection === 2 && (
              <div className="form-section fade-in">
                <div className="flex items-center mb-6">
                  <BookOpen className="w-6 h-6 text-blue-800 mr-3" />
                  <h2 className="form-section-title">Academic Needs Assessment</h2>
                </div>

                <div className="space-y-6">
                  <div>
                    <label className="form-label">
                      How would you rate your current academic performance? *
                    </label>
                    <div className="space-y-2">
                      {['Excellent', 'Good', 'Average', 'Below Average', 'Poor'].map((option) => (
                        <label key={option} className="flex items-center">
                          <input
                            type="radio"
                            value={option}
                            {...register('academicPerformance', { required: 'Please select an option' })}
                            className="mr-3"
                          />
                          {option}
                        </label>
                      ))}
                    </div>
                    {errors.academicPerformance && (
                      <p className="form-error">{errors.academicPerformance.message}</p>
                    )}
                  </div>

                  <div>
                    <label className="form-label">
                      Which subjects do you find most challenging? (Check all that apply)
                    </label>
                    <div className="grid grid-cols-2 gap-2">
                      {['Mathematics', 'Science', 'English', 'Filipino', 'Social Studies', 'Other Languages'].map((subject) => (
                        <label key={subject} className="flex items-center">
                          <input
                            type="checkbox"
                            value={subject}
                            {...register('challengingSubjects')}
                            className="mr-3"
                          />
                          {subject}
                        </label>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="form-label">
                      What type of academic support do you need most? *
                    </label>
                    <div className="space-y-2">
                      {[
                        'Study skills and time management',
                        'Tutoring in specific subjects',
                        'Test-taking strategies',
                        'Note-taking techniques',
                        'Research and writing skills',
                        'None at this time'
                      ].map((option) => (
                        <label key={option} className="flex items-center">
                          <input
                            type="radio"
                            value={option}
                            {...register('academicSupport', { required: 'Please select an option' })}
                            className="mr-3"
                          />
                          {option}
                        </label>
                      ))}
                    </div>
                    {errors.academicSupport && (
                      <p className="form-error">{errors.academicSupport.message}</p>
                    )}
                  </div>

                  <div>
                    <label className="form-label">
                      Describe any specific academic challenges you're facing:
                    </label>
                    <textarea
                      className="form-input"
                      rows="4"
                      {...register('academicChallenges')}
                      placeholder="Please describe any academic difficulties or concerns..."
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Section 3: Personal and Social Needs */}
            {currentSection === 3 && (
              <div className="form-section fade-in">
                <div className="flex items-center mb-6">
                  <Heart className="w-6 h-6 text-blue-800 mr-3" />
                  <h2 className="form-section-title">Personal and Social Needs</h2>
                </div>

                <div className="space-y-6">
                  <div>
                    <label className="form-label">
                      How would you describe your current stress level? *
                    </label>
                    <div className="space-y-2">
                      {['Very Low', 'Low', 'Moderate', 'High', 'Very High'].map((level) => (
                        <label key={level} className="flex items-center">
                          <input
                            type="radio"
                            value={level}
                            {...register('stressLevel', { required: 'Please select an option' })}
                            className="mr-3"
                          />
                          {level}
                        </label>
                      ))}
                    </div>
                    {errors.stressLevel && (
                      <p className="form-error">{errors.stressLevel.message}</p>
                    )}
                  </div>

                  <div>
                    <label className="form-label">
                      What areas of personal development interest you? (Check all that apply)
                    </label>
                    <div className="grid grid-cols-2 gap-2">
                      {[
                        'Self-confidence building',
                        'Communication skills',
                        'Leadership development',
                        'Stress management',
                        'Time management',
                        'Relationship skills',
                        'Emotional regulation',
                        'Goal setting'
                      ].map((area) => (
                        <label key={area} className="flex items-center">
                          <input
                            type="checkbox"
                            value={area}
                            {...register('personalDevelopment')}
                            className="mr-3"
                          />
                          {area}
                        </label>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="form-label">
                      How comfortable do you feel seeking help when you need it? *
                    </label>
                    <div className="space-y-2">
                      {[
                        'Very comfortable',
                        'Somewhat comfortable',
                        'Neutral',
                        'Somewhat uncomfortable',
                        'Very uncomfortable'
                      ].map((level) => (
                        <label key={level} className="flex items-center">
                          <input
                            type="radio"
                            value={level}
                            {...register('seekingHelp', { required: 'Please select an option' })}
                            className="mr-3"
                          />
                          {level}
                        </label>
                      ))}
                    </div>
                    {errors.seekingHelp && (
                      <p className="form-error">{errors.seekingHelp.message}</p>
                    )}
                  </div>

                  <div>
                    <label className="form-label">
                      Is there anything specific you'd like to discuss with a counselor?
                    </label>
                    <textarea
                      className="form-input"
                      rows="4"
                      {...register('counselorDiscussion')}
                      placeholder="Please share any concerns, questions, or topics you'd like to discuss..."
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Section 4: Goals and Preferences */}
            {currentSection === 4 && (
              <div className="form-section fade-in">
                <div className="flex items-center mb-6">
                  <Users className="w-6 h-6 text-blue-800 mr-3" />
                  <h2 className="form-section-title">Goals and Service Preferences</h2>
                </div>

                <div className="space-y-6">
                  <div>
                    <label className="form-label">
                      What are your main goals for this school year? *
                    </label>
                    <textarea
                      className="form-input"
                      rows="3"
                      {...register('schoolYearGoals', { required: 'Please share your goals' })}
                      placeholder="Describe your academic, personal, or career goals for this year..."
                    />
                    {errors.schoolYearGoals && (
                      <p className="form-error">{errors.schoolYearGoals.message}</p>
                    )}
                  </div>

                  <div>
                    <label className="form-label">
                      Which guidance services are you most interested in? (Check all that apply)
                    </label>
                    <div className="grid grid-cols-2 gap-2">
                      {[
                        'Academic counseling',
                        'Career guidance',
                        'Personal counseling',
                        'Crisis intervention',
                        'Group counseling',
                        'Testing and assessment',
                        'College preparation',
                        'Scholarship assistance'
                      ].map((service) => (
                        <label key={service} className="flex items-center">
                          <input
                            type="checkbox"
                            value={service}
                            {...register('interestedServices')}
                            className="mr-3"
                          />
                          {service}
                        </label>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="form-label">
                      How would you prefer to receive guidance services? *
                    </label>
                    <div className="space-y-2">
                      {[
                        'Individual counseling sessions',
                        'Group counseling sessions',
                        'Classroom presentations',
                        'Online resources and materials',
                        'Peer support groups',
                        'Combination of methods'
                      ].map((preference) => (
                        <label key={preference} className="flex items-center">
                          <input
                            type="radio"
                            value={preference}
                            {...register('servicePreference', { required: 'Please select a preference' })}
                            className="mr-3"
                          />
                          {preference}
                        </label>
                      ))}
                    </div>
                    {errors.servicePreference && (
                      <p className="form-error">{errors.servicePreference.message}</p>
                    )}
                  </div>

                  <div>
                    <label className="form-label">
                      Additional comments or information:
                    </label>
                    <textarea
                      className="form-input"
                      rows="4"
                      {...register('additionalComments')}
                      placeholder="Any other information you'd like us to know..."
                    />
                  </div>

                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="flex items-start">
                      <AlertCircle className="w-5 h-5 text-blue-600 mr-3 mt-0.5 flex-shrink-0" />
                      <div className="text-sm text-blue-800">
                        <p className="font-medium mb-1">Confidentiality Notice</p>
                        <p>
                          All information provided in this assessment will be kept confidential and used only 
                          to provide you with appropriate guidance services. Information will only be shared 
                          with other school personnel on a need-to-know basis for your educational benefit.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Navigation Buttons */}
            <div className="flex justify-between items-center pt-6 border-t border-gray-200">
              <button
                type="button"
                onClick={prevSection}
                disabled={currentSection === 1}
                className={`flex items-center px-6 py-3 rounded-lg font-medium transition-colors ${
                  currentSection === 1
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Previous
              </button>

              {currentSection < totalSections ? (
                <button
                  type="button"
                  onClick={nextSection}
                  className="btn-primary"
                >
                  Next Section
                  <ArrowLeft className="w-4 h-4 ml-2 rotate-180" />
                </button>
              ) : (
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="btn-primary flex items-center"
                >
                  {isSubmitting ? (
                    <>
                      <div className="spinner w-4 h-4 mr-2"></div>
                      Submitting...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Submit Assessment
                    </>
                  )}
                </button>
              )}
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default StudentNeedsAssessmentForm;